/**
 * client/game/app.ts — application shell: menus -> session (worker or ws) -> load -> frame loop.
 *
 * OWNER: INTEGRATION agent. Modules are obtained ONLY through their factories
 * (client/<module>/index.ts) and used ONLY through client/types.ts interfaces.
 *
 * Frame order (docs/ARCHITECTURE.md §4):
 *   session.frame (input -> cmds -> predict/send; clock; interp; events)
 *   -> camera.update -> viewmodel.update -> renderer.render -> hud.update -> audio.update -> overlay
 *
 * URL params: ?solo=1&bots=N (auto single player), ?room=CODE (auto join), ?name=,
 *             ?lag=&jitter=&loss= (LagSimulator), ?debug=0 (hide overlay),
 *             ?world=flat|steps (dev test collision world instead of the map's),
 *             ?prematch=N (prematch seconds; default = the real 15 s +- 2 s, #G2),
 *             ?timelimit=N (minutes, default 5 [SAS §1]), ?difficulty=recruit|regular|hardened|veteran (#B3),
 *             ?botmode=sns|faithful (#B3), ?password= (server password, read by the session).
 */
import { loadCollisionWorld } from '@shared/collision/loader';
import { createTestWorld } from '@shared/collision/testworlds';
import type { MapName, RoomSettings, ServerControl } from '@shared/protocol/control';
import type { Transport } from '@shared/protocol/transport';
import { MatchPhase } from '@shared/types';
import { createAudio } from '../audio';
import { createCamera } from '../camera';
import { NetDebugOverlay } from '../debug/overlay';
import { createHud } from '../hud';
import { createInput } from '../input';
import { createFetchAssetSource } from '../net/assets-fetch';
import { ClientSession } from '../net/session';
import { LagSimulator, WorkerTransport, WsTransport, defaultWsUrl, lagParamsFromUrl } from '../net/transport';
import { createRenderer } from '../render';
import { loadSettings, saveSettings, type Settings } from '../settings/settings';
import type {
  AudioEngine, BotDifficulty, CameraController, GameRenderer, Hud, InputSource, MenuAction, MenuSystem, RefDef, RenderInfo, Viewmodel,
} from '../types';
import { createMenus } from '../ui';
import { createViewmodel } from '../viewmodel';
import { SnsDebugHook } from './debughook';
import { Killcam, withPhase } from './killcam';
import { overlayVisible } from './flags';

/** Debug handle for tests / console (window.__game). */
export interface GameDebugHandle {
  state: string;
  fps: number;
  frames: number;
  mapLoaded: boolean;
  session: ClientSession | null;
  renderInfo: RenderInfo | null;
  error: string | null;
  /** Killcam replay state (tests / console): active, final, info (attacker, deathTime...), stats. */
  killcam?: Killcam;
}

/** Ignore a pause request this soon after resuming (the Esc that closed the menu also latches 'pause'). */
const PAUSE_DEBOUNCE_MS = 300;

export class App {
  private readonly settings: Settings = loadSettings();
  private readonly menus: MenuSystem = createMenus();
  private readonly renderer: GameRenderer = createRenderer();
  private readonly input: InputSource = createInput();
  private readonly camera: CameraController = createCamera();
  private readonly viewmodel: Viewmodel = createViewmodel();
  private readonly hud: Hud = createHud();
  private readonly audio: AudioEngine = createAudio();
  private readonly overlay = new NetDebugOverlay();
  private session: ClientSession | null = null;
  private workerTransport: WorkerTransport | null = null;
  private canvas!: HTMLCanvasElement;
  private banner!: HTMLDivElement;
  private lastFrame = 0;
  private fpsAccum = 0;
  private fpsFrames = 0;
  private running = false;
  private solo = false;
  private lastRefdef: RefDef | null = null;
  private resumedAt = -1e9;
  readonly debug: GameDebugHandle = { state: 'menu', fps: 0, frames: 0, mapLoaded: false, session: null, renderInfo: null, error: null };
  /** Read-only game-state probe for tests and the console (window.__sns). */
  readonly sns = new SnsDebugHook();
  /** Killcam replay (respawn + final killcam) from the client's own archive of the last seconds. */
  readonly killcam = new Killcam();
  /** `end` that arrived while the (slow-motion) final killcam was still playing; shown when it ends. */
  private pendingEnd: Extract<ServerControl, { t: 'end' }> | null = null;
  private pendingEndAt = 0;

  constructor(private readonly root: HTMLElement) {}

  start(): void {
    const q = new URLSearchParams(location.search);
    if (q.get('name')) this.settings.name = q.get('name')!;
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'game-canvas';
    this.root.appendChild(this.canvas);
    const ui = document.createElement('div');
    ui.className = 'ui-layer';
    this.root.appendChild(ui);
    this.renderer.init(this.canvas);
    this.renderer.setViewmodel(this.viewmodel);
    this.viewmodel.bindFeedback(this.audio, this.input);
    this.hud.mount(ui);
    this.hud.setVisible(false);
    this.overlay.mount(ui);
    this.overlay.setVisible(overlayVisible(this.settings.showDebugOverlay, q.get('debug')));
    this.menus.mount(ui);
    this.menus.onAction((a) => this.onMenu(a));
    // #N1.4: "Reconnecting..." banner while the session retries a dropped socket
    this.banner = document.createElement('div');
    this.banner.className = 'net-banner';
    this.banner.style.cssText = 'position:absolute;top:12%;left:0;right:0;text-align:center;font:600 20px sans-serif;color:#fff;'
      + 'text-shadow:0 0 4px #000;pointer-events:none;display:none;z-index:50';
    ui.appendChild(this.banner);
    this.input.attach(this.canvas);
    window.addEventListener('resize', () => this.resize());
    // Web Audio unlocks only inside a user gesture: (re)try init on the first gestures.
    const unlock = () => { void this.audio.init(); };
    window.addEventListener('pointerdown', unlock, { capture: true });
    window.addEventListener('keydown', unlock, { capture: true });
    document.addEventListener('visibilitychange', () => this.workerTransport?.setPaused(document.hidden));
    setInterval(() => this.watchSession(), 250); // #H2: closed sessions -> error screen
    this.resize();
    this.sns.bind(() => ({ app: this.debug, menus: this.menus, session: this.session }));
    this.debug.killcam = this.killcam;
    console.log(`[app] secure context: ${window.isSecureContext}, gamepad API: ${'getGamepads' in navigator}`);
    const difficulty = urlDifficulty();
    if (q.get('solo')) this.onMenu({ a: 'solo', bots: Number(q.get('bots') ?? 3), map: 'mp_hijacked', difficulty });
    else if (q.get('room')) this.onMenu({ a: 'join', code: q.get('room')!.toUpperCase() });
    else this.menus.show('main');
  }

  private resize(): void {
    const w = this.root.clientWidth || window.innerWidth, h = this.root.clientHeight || window.innerHeight;
    const pr = Math.min(2, window.devicePixelRatio || 1) * this.settings.renderScale;
    this.renderer.resize(w, h, pr);
    this.viewmodel.setAspect(w / h);
  }

  private onMenu(a: MenuAction): void {
    switch (a.a) {
      case 'solo': {
        this.solo = true;
        const wt = new WorkerTransport();
        this.workerTransport = wt;
        const s = this.connect(wrapLag(wt), 'worker');
        s.hello();
        s.create(a.map, roomSettings(a.bots, a.difficulty));
        s.onControl((m) => { if (m.t === 'welcome' && s.state === 'lobby' && !s.load) s.start(); });
        this.menus.show('loading');
        break;
      }
      case 'create': {
        this.solo = false;
        const s = this.connect(wrapLag(new WsTransport(defaultWsUrl())), 'ws');
        s.hello();
        s.create(a.map, roomSettings(a.bots, a.difficulty));
        this.menus.show('lobby', null);
        break;
      }
      case 'join': {
        this.solo = false;
        const s = this.connect(wrapLag(new WsTransport(defaultWsUrl())), 'ws');
        s.hello(a.code);
        this.menus.show('lobby', null);
        break;
      }
      case 'start':
        this.session?.start();
        break;
      case 'resume':
        this.menus.show('none');
        this.resumedAt = performance.now();
        void this.canvas.requestPointerLock?.();
        break;
      case 'leave': {
        const s = this.session;
        // #H1.3 / #N1.3: online, BACK on the after-action report returns to the room's lobby (same code,
        // same people) instead of leaving it; the host brings the room back to 'lobby' 10 s after `end`.
        if (s && !this.solo && this.menus.current === 'end') {
          this.showLobby(s);
          break;
        }
        s?.close();
        this.session = null;
        this.workerTransport = null;
        this.running = false;
        this.hud.setVisible(false);
        this.input.setCollisionWorld?.(null);
        this.camera.setCollisionWorld?.(null);
        this.debug.state = 'menu';
        this.menus.show('main');
        break;
      }
      case 'settings':
        Object.assign(this.settings, a.settings);
        saveSettings(this.settings);
        this.overlay.setVisible(overlayVisible(this.settings.showDebugOverlay, new URLSearchParams(location.search).get('debug')));
        this.audio.setVolumes(this.settings.volume);
        break;
      case 'hidRumble':
        void this.input.requestHidRumble?.();
        break;
      // #H2 (hud-ui-audio, additive): lobby settings (leader) and ready toggle
      case 'roomSettings':
        this.session?.send({ t: 'settings', settings: a.settings });
        break;
      case 'ready':
        this.session?.setReady(a.ready);
        break;
    }
  }

  /**
   * #H2 (hud-ui-audio, additive): a fatal server error (bad/expired room code, wrong password, full room,
   * version mismatch, shutdown) or a session that closed for good (server restart, reconnect gave up) shows the
   * menus' error screen (message + way back) instead of leaving the player on CONNECTING or in a frozen world.
   */
  private showNetError(s: ClientSession, code: string | undefined, msg: string): void {
    if (s !== this.session || this.menus.current === 'error') return;
    this.running = false;
    this.hud.setVisible(false);
    this.banner.style.display = 'none';
    document.exitPointerLock?.();
    this.debug.state = 'error';
    this.debug.error = code ? `${code}: ${msg}` : msg;
    const room = s.room?.code ?? new URLSearchParams(location.search).get('room')?.toUpperCase() ?? null;
    this.menus.show('error', { code, msg, room });
  }

  private watchSession(): void {
    const s = this.session;
    if (!s || s.state !== 'closed' || this.debug.state === 'menu' || this.debug.state === 'error') return;
    const m = /^([A-Z_]+): (.*)$/.exec(s.lastError ?? '');
    this.showNetError(s, m?.[1], m?.[2] ?? s.lastError ?? 'connection closed');
  }

  private connect(t: Transport, label: string): ClientSession {
    this.session?.close();
    this.killcam.reset();
    this.pendingEnd = null;
    const s = new ClientSession(t, this.settings.name, label);
    this.session = s;
    this.debug.session = s;
    this.debug.state = 'connecting';
    this.sns.reset();
    s.onControl((m) => this.onControl(s, m));
    return s;
  }

  private showLobby(s: ClientSession): void {
    this.hud.setVisible(false);
    this.debug.state = 'lobby';
    document.exitPointerLock?.();
    // #H1.4: pass the local client number so the lobby greys out START MATCH for non-leaders
    this.menus.show('lobby', { room: s.room, localClientNum: s.world.localClientNum });
  }

  private onControl(s: ClientSession, m: ServerControl): void {
    if (s !== this.session) return;
    if (m.t === 'room' && !this.solo) {
      // lobby screen refresh, and #N1.3: the room came back to the lobby after a match
      if (this.menus.current === 'lobby' || (s.state === 'lobby' && m.room.phase === 'lobby' && this.debug.state !== 'lobby' && this.menus.current !== 'end' && this.menus.current !== 'options')) {
        this.showLobby(s);
      }
    }
    if (m.t === 'error') {
      this.debug.error = `${m.code}: ${m.msg}`; console.warn('[server]', m.code, m.msg);
      // #H2: fatal codes end the session (reconnect failures are decided by the session -> watchSession)
      if (FATAL_ERRORS.has(m.code) && s.state !== 'reconnecting') this.showNetError(s, m.code, m.msg);
    }
    if (m.t === 'load') void this.loadMatch(s, m.map);
    if (m.t === 'end') {
      // The host sends `end` when the gametype enters INTERMISSION, i.e. after the 7 s wager outcome splash
      // (POSTGAME, drawn by the HUD) and the final killcam [SAS §9], so the after-action report shows now (#H1.2).
      // The final killcam plays its slow motion (killcam-time.ts), so it runs past the server's 7 s FINAL_KILLCAM
      // phase: the after-action report waits for it (integration).
      const left = this.killcam.finalRemainingMs(performance.now());
      if (left > 0) { this.pendingEnd = m; this.pendingEndAt = performance.now() + left; return; }
      this.showEnd(m);
    }
  }

  private showEnd(m: Extract<ServerControl, { t: 'end' }>): void {
    this.pendingEnd = null;
    this.killcam.stop('end');
    this.sns.onEnd(m);
    this.menus.show('end', m);
    this.debug.state = 'ended';
    document.exitPointerLock?.();
  }

  private async loadMatch(s: ClientSession, map: MapName): Promise<void> {
    this.debug.state = 'loading';
    this.killcam.reset();
    this.pendingEnd = null;
    this.menus.show('loading');
    // #H2 (hud-ui-audio): on the first load, keep the Hijacked loading screen up until its loading.lua sequence
    // (title fades from 2 s, art + tip box fade in at 5-6 s) has played; a fast local load otherwise shows only
    // black. Skipped when ?prematch= is given (dev/test runs). The hold itself is a remake choice (ESTIMATE).
    const holdMs = !this.debug.mapLoaded && !new URLSearchParams(location.search).has('prematch') ? LOADING_SEQUENCE_MS : 0;
    const hold = new Promise<void>((res) => setTimeout(res, holdMs));
    try {
      const testWorld = s.room?.settings.testWorld;
      const [world] = await Promise.all([
        testWorld ? Promise.resolve(createTestWorld(testWorld)) : loadCollisionWorld(createFetchAssetSource(), map),
        this.debug.mapLoaded ? Promise.resolve() : this.renderer.loadMap(map, (f, l) => this.menus.setLoading(f, l)),
        this.viewmodel.load(),
        hold,
      ]);
      void this.audio.init(); // never block loading on audio (no-gesture AudioContext stays suspended)
      this.debug.mapLoaded = true;
      if (s !== this.session) return;
      s.setCollisionWorld(world);
      this.input.setCollisionWorld?.(world); // #input-1.1: aim-assist visibility traces
      this.camera.setCollisionWorld?.(world); // #V5: death cam wall trace
      s.sendLoaded();
      this.menus.show('none');
      this.hud.setVisible(true);
      this.debug.state = 'playing';
      if (!this.running) { this.running = true; this.lastFrame = performance.now(); requestAnimationFrame((t) => this.frame(t)); }
    } catch (e) {
      this.debug.error = String(e);
      console.error('[app] load failed', e);
    }
  }

  private frame(now: number): void {
    if (!this.running) return;
    requestAnimationFrame((t) => this.frame(t));
    const dtMs = Math.min(100, now - this.lastFrame);
    this.lastFrame = now;
    this.fpsAccum += dtMs; this.fpsFrames++;
    if (this.fpsAccum >= 500) { this.debug.fps = (this.fpsFrames * 1000) / this.fpsAccum; this.fpsAccum = 0; this.fpsFrames = 0; }
    this.debug.frames++;
    const s = this.session;
    if (!s) return;
    this.banner.style.display = s.state === 'reconnecting' ? 'block' : 'none';
    if (s.state === 'reconnecting') this.banner.textContent = 'Reconnecting...';
    if (this.debug.state === 'lobby') return; // back in the lobby between matches: nothing to simulate or draw
    const world = s.world;
    // #input-1.4 / #N1.1: any open menu (pause, options, end) suspends gameplay input; cmds keep flowing
    s.inputSuspended = this.menus.current !== 'none';
    // #input-1.2: the live (previous frame's) vertical FOV, ADS-lerped by the camera
    const fovY = this.lastRefdef?.fovY ?? 51.08;
    const ictx = { world, settings: this.settings, fovY, adsFrac: world.predicted.adsFrac, dtMs, suspended: s.inputSuspended }; // #input-2
    s.frame(now, this.input, ictx, this.settings.timeNudge);
    // world.frameEvents is filled only now: event-driven rumble (CTL §7) runs after the session frame
    this.input.postFrame?.(ictx);
    // #input-1.3 / #H1.1: pause menu (Esc, pointer-lock release, Options button)
    const f = s.lastInputFrame;
    if (f?.pause && this.menus.current === 'none' && this.debug.state === 'playing' && now - this.resumedAt > PAUSE_DEBOUNCE_MS) {
      this.menus.show('pause');
      s.inputSuspended = true;
    }
    const refdef = this.camera.update(world, this.settings, dtMs);
    this.lastRefdef = refdef;
    // #V1: the fire kick also moves the aim (added to the next cmds' angles only)
    const k = refdef.kickAngles;
    if (k) { s.aimKickAngles[0] = k[0]; s.aimKickAngles[1] = k[1]; s.aimKickAngles[2] = k[2]; }
    // Killcam: renderer, viewmodel and audio see the archived footage from the attacker's eye; the HUD keeps the
    // live world (it draws the KILLCAM letterbox from the same KILLCAM event).
    const replay = this.killcam.update(world, now);
    const pw = replay ?? world;
    const prd = replay ? this.killcam.view(refdef, this.settings) : refdef;
    this.viewmodel.update(pw, prd, dtMs);
    this.renderer.render(pw, prd, dtMs);
    this.hud.update(replay && this.killcam.final ? withPhase(world, MatchPhase.FINAL_KILLCAM) : world, refdef, this.settings, dtMs);
    this.audio.update(pw, prd, this.settings, dtMs);
    if (this.pendingEnd && (!this.killcam.final || now >= this.pendingEndAt + 500)) this.showEnd(this.pendingEnd);
    this.sns.onFrame(world);
    this.debug.renderInfo = this.renderer.info;
    this.overlay.update(world, this.debug.fps, this.renderer.info);
  }
}

/** ui_mp/t6/hud/loading.lua: map art + tip box finish fading in at 5 s + 1 s (client/style.css .b2-load-*). */
const LOADING_SEQUENCE_MS = 6000;

/** #H2: server errors after which the session cannot continue. */
const FATAL_ERRORS: ReadonlySet<string> = new Set(['ROOM_NOT_FOUND', 'ROOM_FULL', 'VERSION', 'IN_PROGRESS', 'BAD_PASSWORD', 'SHUTDOWN']);

const DIFFICULTIES: readonly BotDifficulty[] = ['recruit', 'regular', 'hardened', 'veteran'];

function urlDifficulty(): BotDifficulty | undefined {
  const d = new URLSearchParams(location.search).get('difficulty');
  return d && (DIFFICULTIES as readonly string[]).includes(d) ? (d as BotDifficulty) : undefined;
}

function roomSettings(bots: number, difficulty?: BotDifficulty): RoomSettings {
  const q = new URLSearchParams(location.search);
  const w = q.get('world');
  // #G2 decision: the faithful 15 s +- 2 s frozen prematch is the default everywhere; ?prematch=N overrides
  // (tests and quick dev runs pass ?prematch=0).
  const pm = q.get('prematch');
  const prematchSec = pm !== null && pm !== '' && Number.isFinite(Number(pm)) ? Number(pm) : undefined;
  const tl = Number(q.get('timelimit'));
  const mode = q.get('botmode');
  return {
    timeLimitMin: Number.isFinite(tl) && tl > 0 ? tl : 5, // [SAS §1] 5 minutes
    scoreLimit: 0,
    bots,
    botDifficulty: urlDifficulty() ?? difficulty ?? 'regular', // URL wins over the menu (tests, shared links)
    ...(mode === 'sns' || mode === 'faithful' ? { botMode: mode } : {}),
    ...(w === 'flat' || w === 'steps' ? { testWorld: w } : {}),
    ...(prematchSec !== undefined ? { prematchSec } : {}),
  };
}

function wrapLag(t: Transport): Transport {
  const p = lagParamsFromUrl();
  return p ? new LagSimulator(t, p) : t;
}
